/* eslint-disable @typescript-eslint/explicit-function-return-type */
import type {Routes} from "@angular/router";
import {PermissionId} from "../../models/users/Permission";
import {UserAccountType} from "../../models/users/User";
import {and, hasPermissions, hasUserTypes, isLoggedIn, isMyUserId, or} from "../../services/guards/permission-guard";
import {PasswordFormType} from "./password/password-form";

export const userRoutes = [
  {
    path: "users",
    canActivateChild: [isLoggedIn],
    children: [
      {
        path: "admins",
        canActivateChild: [hasUserTypes(UserAccountType.Admin)],
        data: {userAccountType: UserAccountType.Admin},
        children: [
          {
            path: "",
            loadComponent: () => import("./list/admin-users-list").then(m => m.AdminUsersList),
          },
          {
            path: "create",
            loadComponent: () => import("./form/user-form").then(m => m.UserForm),
          },
          {
            path: ":userId",
            loadComponent: () => import("./form/user-form").then(m => m.UserForm),
          },
        ],
      },
      {
        path: "bactoforce",
        canActivateChild: [hasUserTypes(UserAccountType.Admin, UserAccountType.Bactoforce)],
        data: {userAccountType: UserAccountType.Bactoforce},
        children: [
          {
            path: "",
            loadComponent: () => import("./list/bactoforce-users-list").then(m => m.BactoforceUsersList),
          },
          {
            path: "create",
            canActivate: [hasPermissions(PermissionId.CreateBactoforceUser)],
            loadComponent: () => import("./form/user-form").then(m => m.UserForm),
          },
          {
            path: ":userId",
            loadComponent: () => import("./form/user-form").then(m => m.UserForm),
          },
        ],
      },
      {
        path: "customers",
        data: {userAccountType: UserAccountType.Customer},
        children: [
          {
            path: "",
            canActivate: [hasUserTypes(UserAccountType.Admin, UserAccountType.Bactoforce)],
            loadComponent: () => import("./list/customer-users-list").then(m => m.CustomerUsersList),
          },
          {
            path: "create",
            canActivate: [hasUserTypes(UserAccountType.Admin, UserAccountType.Bactoforce)],
            loadComponent: () => import("./form/user-form").then(m => m.UserForm),
          },
          {
            path: ":userId",
            canActivate: [
              or(
                hasUserTypes(UserAccountType.Admin, UserAccountType.Bactoforce),
                isMyUserId("userId", UserAccountType.Customer),
              ),
            ],
            loadComponent: () => import("./form/user-form").then(m => m.UserForm),
          },
        ],
      },
      {
        path: "third-party",
        data: {userAccountType: UserAccountType.ThirdParty},
        children: [
          {
            path: "",
            canActivate: [
              hasUserTypes(UserAccountType.Admin, UserAccountType.Bactoforce),
              hasPermissions(PermissionId.ManageThirdPartyUsers),
            ],
            loadComponent: () => import("./list/third-party-users-list").then(m => m.ThirdPartyUsersList),
          },
          {
            path: "create",
            canActivate: [
              hasUserTypes(UserAccountType.Admin, UserAccountType.Bactoforce),
              hasPermissions(PermissionId.ManageThirdPartyUsers),
            ],
            loadComponent: () => import("./form/user-form").then(m => m.UserForm),
          },
          {
            path: ":userId",
            canActivate: [
              or(
                and(
                  hasUserTypes(UserAccountType.Admin, UserAccountType.Bactoforce),
                  hasPermissions(PermissionId.ManageThirdPartyUsers),
                ),
                isMyUserId("userId", UserAccountType.ThirdParty),
              ),
            ],
            loadComponent: () => import("./form/user-form").then(m => m.UserForm),
          },
        ],
      },
    ],
  },

  // Permissions
  {
    path: "permissions",
    canActivateChild: [isLoggedIn, hasUserTypes(UserAccountType.Admin)],
    children: [
      {
        path: "",
        loadComponent: () => import("./permissions/list/user-permissions-list").then(m => m.UserPermissionsList),
      },
      {
        path: "create",
        loadComponent: () => import("./permissions/form/user-permission-form").then(m => m.UserPermissionForm),
      },
    ],
  },

  // Roles
  {
    path: "roles",
    canActivateChild: [isLoggedIn, hasUserTypes(UserAccountType.Admin)],
    children: [
      {
        path: "",
        loadComponent: () => import("./roles/list/roles-list").then(m => m.RolesList),
      },
      {
        path: "create",
        loadComponent: () => import("./roles/form/role-form").then(m => m.RoleForm),
      },
      {
        path: ":roleId",
        loadComponent: () => import("./roles/form/role-form").then(m => m.RoleForm),
      },
    ],
  },

  // Login & Password
  {
    path: "login",
    loadComponent: () => import("./password/password-form").then(m => m.PasswordForm),
    data: {
      formType: PasswordFormType.Login,
    },
  },
  {
    path: "forgot-password",
    loadComponent: () => import("./password/password-form").then(m => m.PasswordForm),
    data: {
      formType: PasswordFormType.ForgotPassword,
    },
  },
  {
    path: "change-password",
    canActivate: [isLoggedIn],
    loadComponent: () => import("./password/password-form").then(m => m.PasswordForm),
    data: {
      formType: PasswordFormType.ChangePassword,
    },
  },
  {
    path: "set-password",
    loadComponent: () => import("./password/password-form").then(m => m.PasswordForm),
    data: {
      formType: PasswordFormType.ResetPassword,
    },
  },
] as const satisfies Routes;
